import React, { useCallback, useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import PropTypes from 'prop-types';
import useForm from 'react-hook-form';
import { shallowEqual, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import TextInputFieldWithValidation from '../../../../../components/ValidationFormElements/TextInputFieldWithValidation';
import ArrowIosIcon from '../../../../../components/ClaimFormComponents/ArrowIosIcon';
import useDeviceResize from '../../../../../hooks/useDeviceResize';
import { cascoPhysicalClaimValidationSchema } from '../../../../../validations/claimValidationSchema/casco/cascoClaimValidationSchema';
import useRegisterValidationFields from '../../../../../hooks/useRegisterValidationFields';
import useUnRegisterValidationFields from '../../../../../hooks/useUnRegisterValidationFields';

import ClaimStepNavigationWrapper from '../../../../../components/ClaimFormComponents/ClaimStepNavigationWrapper';
import SubmitFormWrapper from '../../../../../components/FormComponents/SubmitFormWrapper';
import InputAttentionMessage from '../../../../../components/ClaimFormComponents/InputAttentionMessage';
import {
  handleInputChange,
  passInitialSearchApplicantFieldValues,
  reduceValidationError,
  resolveBackendValidation,
} from '../../../../../helpers/claims';
import { searchApplicant } from '../../../../../helpers/claims/cascoClaim';
import { PHYSICAL_APPLICANT_FIELDS } from '../../../options';
import { APPLICANT_TYPES } from '../../../../../constants';

import useStyles from '../../../../CompensationContainer/useStyles';

const PhysicalSearchCascoApplicantForm = ({ handleSearch, setStepNavigationBox, isSearching }) => {
  const isDeviceMdSize = useDeviceResize('compensationSearchGridMdSize');
  const isDeviceLgSize = useDeviceResize('lg');
  const classes = useStyles({ isDeviceMdSize });
  const { t } = useTranslation();

  const { handleSubmit, register, unregister, setValue, errors, clearError } = useForm({
    validationSchema: cascoPhysicalClaimValidationSchema,
    mode: 'onBlur',
  });

  useRegisterValidationFields(PHYSICAL_APPLICANT_FIELDS, register);
  useUnRegisterValidationFields(PHYSICAL_APPLICANT_FIELDS, unregister);

  const { searchData, searchApplicantFilledFields } = useSelector(
    (store) => store.searchApplicant,
    shallowEqual,
  );
  const { validationError } = searchData;

  const initialPnum = passInitialSearchApplicantFieldValues(
    searchApplicantFilledFields,
    APPLICANT_TYPES[0].value,
    'pnum',
  );

  const initialSsn = passInitialSearchApplicantFieldValues(
    searchApplicantFilledFields,
    APPLICANT_TYPES[0].value,
    'ssn',
  );

  const initialTpnum = passInitialSearchApplicantFieldValues(
    searchApplicantFilledFields,
    APPLICANT_TYPES[0].value,
    'tpnum',
  );

  const initialPolicy = passInitialSearchApplicantFieldValues(
    searchApplicantFilledFields,
    APPLICANT_TYPES[0].value,
    'policy',
  );

  const [initState, setInitState] = useState({
    pnum: initialPnum,
    ssn: initialSsn,
    tpnum: initialTpnum,
    policy: initialPolicy,
  });

  // We pass initState when user redirects to first step,
  // after search applicant, when we already have an applicant
  useEffect(() => {
    setValue('pnum', initState.pnum, !!initState.pnum.length);
    setValue('ssn', initState.ssn, !!initState.ssn.length);
    setValue('tpnum', initState.tpnum, !!initState.tpnum.length);
    setValue('policy', initState.policy, !!initState.policy.length);
  }, [initState, setValue]);

  useEffect(() => {
    resolveBackendValidation(reduceValidationError(validationError));
  }, [validationError]);

  const inputOnChange = useCallback(
    (value, name) => {
      handleInputChange(value, name, setInitState, initState, setValue);
    },
    [initState, setValue],
  );

  const onSubmit = useCallback(
    (data) => {
      searchApplicant(data, clearError, handleSearch);
    },
    [clearError, handleSearch],
  );

  return (
    <SubmitFormWrapper onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={4}>
        <ClaimStepNavigationWrapper
          clearError={clearError}
          errors={errors}
          handleSearch={handleSearch}
          isNextDisabled={isSearching}
          handleSubmit={handleSubmit}
          setStepNavigationBox={setStepNavigationBox}
          isDeviceLgSize={isDeviceLgSize}
          onSubmit={onSubmit}
          styles={classes.searchNavigationButton}
          dependencies={[JSON.stringify(errors), isDeviceLgSize, false]}
        />
        <Grid item md={5} xs={12}>
          <TextInputFieldWithValidation
            required
            formProps={{ errors, clearError }}
            labelKey="searchApplicant.physical.passOrIdNum"
            name="pnum"
            value={initState.pnum}
            handleChange={({ target }) => inputOnChange(target.value, 'pnum')}
          />
          <InputAttentionMessage message={t('warningMessage.provideValidPassport')} />
        </Grid>
        <ArrowIosIcon isDeviceMdSize={isDeviceMdSize} />
        <Grid item md={5} xs={12}>
          <TextInputFieldWithValidation
            required
            formProps={{ errors, clearError }}
            labelKey="searchApplicant.physical.ssn"
            name="ssn"
            value={initState.ssn}
            handleChange={({ target }) => inputOnChange(target.value, 'ssn')}
          />
        </Grid>
        <Grid item xs={12}>
          <div className={classes.divider} />
        </Grid>
        <Grid item md={5} xs={12}>
          <TextInputFieldWithValidation
            required
            formProps={{ errors, clearError }}
            labelKey="searchApplicant.physical.tpnum"
            name="tpnum"
            value={initState.tpnum}
            handleChange={({ target }) => inputOnChange(target.value, 'tpnum')}
          />
        </Grid>
        <ArrowIosIcon isDeviceMdSize={isDeviceMdSize} />
        <Grid item md={5} xs={12}>
          <TextInputFieldWithValidation
            required
            formProps={{ errors, clearError }}
            labelKey="searchApplicant.physical.policy"
            name="policy"
            value={initState.policy}
            handleChange={({ target }) => inputOnChange(target.value, 'policy')}
          />
        </Grid>
      </Grid>
    </SubmitFormWrapper>
  );
};

PhysicalSearchCascoApplicantForm.propTypes = {
  handleSearch: PropTypes.func.isRequired,
  setStepNavigationBox: PropTypes.func.isRequired,
  isSearching: PropTypes.bool.isRequired,
};

export default PhysicalSearchCascoApplicantForm;
